import * as React from 'react';
import { useNavigate } from 'react-router';
import Dialog from '@mui/material/Dialog';
import CircularProgress from '@mui/material/CircularProgress';
import { useColorScheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { requirementsApi } from '../../services/api';
import iamApi from '../../services/iamApi';
import { usePref } from '../../services/prefs';
import { getRecentItems } from '../../utils/recentItems';
import { useTypeMap } from '../../hooks/useTypeMap';
import IdChip from '../common/IdChip';
import ScopeToggle from '../common/ScopeToggle';
import ProjectBadge from '../common/ProjectBadge';
import TypeBadge from '../common/TypeBadge';
import ItemTooltip from '../itemCard/ItemTooltip';
import { Ic } from './Icons';

const DEBOUNCE_MS = 200;

// #150 — the ⌘K command palette: fuzzy jump-to (id+title) merged with a small
// set of run-actions (New, Import, switch project, toggle theme), plus recents
// when the query is empty. Opened/closed from AppShell (global ⌘K/Ctrl+K).
export default function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const { activeOrg } = useAuth();
  const { mode, systemMode, setMode } = useColorScheme();
  const showHumanIds = usePref('showHumanIds');

  const [query, setQuery] = React.useState('');
  // Search scope: 'text' = keyword id+title (default); 'vector' = semantic search.
  // Tab toggles it (COMMAND_PALETTE.md). Vector results depend on the semantic index
  // being populated (#223) — an empty result is handled gracefully below.
  // (Named `scope`, not `mode`, to avoid colliding with useColorScheme's `mode`.)
  const [scope, setScope] = React.useState('text');
  const [itemResults, setItemResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [projects, setProjects] = React.useState([]);
  const [highlight, setHighlight] = React.useState(0);
  const inputRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const seqRef = React.useRef(0);

  // Reset on open/close; load the project list once per open (cheap, small — for
  // the "Switch to project X" actions) and focus the input.
  React.useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setScope('text');
    setItemResults([]);
    setHighlight(0);
    // #867: this had no cancellation guard, so a project-list fetch that resolved
    // AFTER the palette closed/unmounted (a fast Ctrl+K toggle, or navigating away
    // mid-fetch) still called setProjects on an unmounted component — surfaced by
    // React's "not wrapped in act(...)" warning in tests, the same real defect
    // class useTypeMap's `cancelled` guard already exists to prevent (useTypeMap.js).
    let cancelled = false;
    if (activeOrg) {
      Promise.resolve(iamApi.projects(activeOrg))
        .then((rows) => { if (!cancelled) setProjects(Array.isArray(rows) ? rows : []); })
        .catch(() => { if (!cancelled) setProjects([]); });
    }
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, activeOrg]);

  const resolved = (mode === 'system' ? systemMode : mode) || 'light';

  // Search is cross-project (tenant-wide), so a multi-project user needs to see which
  // project each match belongs to. Derive the id→name map from the project list we
  // already fetch for the "Switch to project" actions; only label when >1 project exists.
  const projectMap = React.useMemo(() => {
    const m = {};
    projects.forEach((p) => { if (p && p.id) m[p.id] = p.name || p.title || p.id; });
    return m;
  }, [projects]);
  const showProject = projects.length > 1;
  const projectName = (r) => (showProject ? projectMap[r.project_id] : null);
  const typeMap = useTypeMap();

  // Navigating to an item (clicking a result, or clicking the id in its hover-preview
  // tooltip) changes the `#item=` hash — close the palette when that happens so a
  // tooltip-driven navigation doesn't leave the palette open over the item.
  React.useEffect(() => {
    if (!open) return undefined;
    const onHash = () => { if (window.location.hash.includes('item=')) onClose(); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [open, onClose]);

  const goToItem = React.useCallback((item) => {
    onClose();
    if (window.location.pathname !== '/') navigate('/');
    window.location.hash = `item=${item.id}`;
  }, [navigate, onClose]);

  const newItem = React.useCallback((isContainer) => {
    onClose();
    const dispatch = () => window.dispatchEvent(new CustomEvent('rmdv:new-item', { detail: { isContainer } }));
    if (window.location.pathname !== '/') {
      navigate('/');
      // RequirementsTreeView (the ".cols" root) mounts asynchronously after the
      // route change — a single fixed-delay setTimeout was a race (slow first
      // paint, e.g. while TinyMCE/other views are still unloading, silently
      // dropped the event). Poll for its mount instead, bounded so a genuine
      // failure doesn't retry forever.
      let attempts = 0;
      const tryDispatch = () => {
        attempts += 1;
        if (document.querySelector('.cols') || attempts > 20) dispatch();
        else setTimeout(tryDispatch, 50);
      };
      setTimeout(tryDispatch, 30);
    } else {
      dispatch();
    }
  }, [navigate, onClose]);

  // Static actions, filtered by the typed query like everything else. Each has a
  // `run` handler and a `keywords` string (matched in addition to the label) so
  // e.g. typing "dark" finds "Toggle theme".
  const actions = React.useMemo(() => {
    const list = [
      {
        id: 'action-new-requirement', icon: 'plus', label: 'New Requirement',
        keywords: 'create add requirement', run: () => newItem(false),
      },
      {
        id: 'action-new-container', icon: 'folder', label: 'New Container',
        keywords: 'create add container folder', run: () => newItem(true),
      },
      {
        id: 'action-import', icon: 'import', label: 'Import documents',
        keywords: 'document upload bulk', run: () => { onClose(); navigate('/document-import'); },
      },
      {
        id: 'go-dashboard', icon: 'grid', label: 'Go to Dashboard',
        keywords: 'dashboard analytics coverage metrics', run: () => { onClose(); navigate('/analytics'); },
      },
      {
        id: 'go-vector', icon: 'nodes', label: 'Go to Vector space',
        keywords: 'vector semantic embeddings space', run: () => { onClose(); navigate('/vector-space'); },
      },
      {
        id: 'go-settings', icon: 'gear', label: 'Go to Settings',
        keywords: 'settings preferences account api keys display', run: () => { onClose(); navigate('/account/settings'); },
      },
      {
        id: 'action-theme', icon: resolved === 'dark' ? 'sun' : 'moon',
        label: resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
        keywords: 'theme dark light appearance',
        run: () => { onClose(); setMode(resolved === 'dark' ? 'light' : 'dark'); },
      },
      ...projects.map((p) => ({
        id: `action-project-${p.id}`, icon: 'users', label: `Switch to project: ${p.name}`,
        keywords: 'project switch org', run: () => { onClose(); navigate('/'); },
      })),
    ];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((a) => `${a.label} ${a.keywords}`.toLowerCase().includes(q));
  }, [query, projects, resolved, newItem, navigate, onClose, setMode]);

  // Debounced requirement search for the typed query. Text mode = keyword id+title
  // jump-to (`quickSearch`); Vector mode = semantic (`vectorSearch`). Re-runs when the
  // scope toggles so switching Text↔Vector re-queries the same word. The seq guard makes
  // it latest-wins (a slow response from an earlier query/mode can't clobber newer results).
  React.useEffect(() => {
    const q = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q) { setItemResults([]); setLoading(false); return undefined; }
    setLoading(true);
    const mySeq = ++seqRef.current;
    const searchFn = scope === 'vector' ? requirementsApi.vectorSearch : requirementsApi.quickSearch;
    debounceRef.current = setTimeout(() => {
      searchFn(q).then((rows) => {
        if (seqRef.current !== mySeq) return;
        setItemResults(rows);
        setLoading(false);
      }).catch(() => {
        if (seqRef.current !== mySeq) return;
        setItemResults([]);
        setLoading(false);
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query, scope]);

  // Re-read on every open (not just once) so an item viewed since the palette
  // was last closed shows up here — a plain useMemo keyed on `query` alone
  // wouldn't refresh when reopening with the same (empty) query.
  const [recentsSnapshot, setRecentsSnapshot] = React.useState([]);
  React.useEffect(() => { if (open) setRecentsSnapshot(getRecentItems()); }, [open]);
  const recents = React.useMemo(
    () => (query.trim() ? [] : recentsSnapshot),
    [query, recentsSnapshot]
  );

  // One flat list backs keyboard nav (Up/Down/Enter) regardless of section.
  const flatEntries = React.useMemo(() => {
    const entries = [];
    if (!query.trim()) {
      recents.forEach((r) => entries.push({ kind: 'item', data: r }));
    }
    itemResults.forEach((r) => entries.push({ kind: 'item', data: r }));
    actions.forEach((a) => entries.push({ kind: 'action', data: a }));
    return entries;
  }, [query, recents, itemResults, actions]);

  React.useEffect(() => { setHighlight(0); }, [flatEntries.length]);

  const runEntry = (entry) => {
    if (!entry) return;
    if (entry.kind === 'item') goToItem(entry.data);
    else entry.data.run();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'Tab') {
      // Tab cycles the scope toggle (Text ⟷ Vector) — COMMAND_PALETTE.md.
      e.preventDefault();
      setScope((s2) => (s2 === 'text' ? 'vector' : 'text'));
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(flatEntries.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runEntry(flatEntries[highlight]);
    }
  };

  // #170 (a11y): the results list is an ARIA listbox owned by the combobox
  // input. Options carry `cp-opt-<n>` ids so `aria-activedescendant` can point
  // the screen reader at the highlighted row without moving DOM focus off the
  // input (the ⌘K jump-to pattern).
  const LISTBOX_ID = 'command-palette-listbox';
  const activeDescId = flatEntries.length > 0 ? `cp-opt-${highlight}` : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="command-palette"
      aria-labelledby="command-palette-title"
      slotProps={{ paper: { sx: { position: 'fixed', top: 96, m: 0, borderRadius: 2 } } }}
    >
      {/* #170 (a11y): the dialog needs an accessible name — there is no visible
          title bar, so name it via a visually-hidden label the Paper's
          aria-labelledby points at. */}
      <span
        id="command-palette-title"
        style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}
      >
        Command palette
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
        <Ic name="search" sm />
        <input
          ref={inputRef}
          data-testid="command-palette-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Jump to an item, or run a command…"
          aria-label="Command palette"
          role="combobox"
          aria-expanded={open}
          aria-controls={LISTBOX_ID}
          aria-autocomplete="list"
          aria-activedescendant={activeDescId}
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontSize: 15, color: 'inherit', fontFamily: 'inherit',
          }}
        />
        {loading && <CircularProgress size={14} thickness={5} />}
        <ScopeToggle mode={scope} onChange={setScope} />
        <span className="kbd">Esc</span>
      </div>
      <div
        id={LISTBOX_ID}
        role="listbox"
        aria-label="Results and actions"
        style={{ maxHeight: 420, overflowY: 'auto', padding: 6 }}
      >
        {!query.trim() && recents.length > 0 && (
          <div role="presentation" className="t-over" style={{ padding: '6px 10px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>
            Recent
          </div>
        )}
        {recents.map((r) => {
          const idx = flatEntries.findIndex((e) => e.kind === 'item' && e.data.id === r.id);
          return (
            <PaletteRow
              key={`recent-${r.id}`}
              id={`cp-opt-${idx}`}
              active={idx === highlight}
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => goToItem(r)}
              icon={<Ic name="clock" sm />}
            >
              <ItemTooltip item={{ id: r.id, display_id: r.display_id, title: r.title, type: typeMap[r.type_id] }}>
                <IdChip id={r.id} displayId={r.display_id} showHumanIds={showHumanIds} disableTooltip />
              </ItemTooltip>
              <span className="palette-row-title">{r.title}</span>
              <TypeBadge label={typeMap[r.type_id]} />
              <ProjectBadge name={projectName(r)} />
            </PaletteRow>
          );
        })}

        {query.trim() && itemResults.length > 0 && (
          <div role="presentation" className="t-over" style={{ padding: '6px 10px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>
            {scope === 'vector' ? 'Semantic matches' : 'Requirements'}
          </div>
        )}
        {itemResults.map((r) => {
          const idx = flatEntries.findIndex((e) => e.kind === 'item' && e.data.id === r.id);
          return (
            <PaletteRow
              key={r.id}
              id={`cp-opt-${idx}`}
              active={idx === highlight}
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => goToItem(r)}
            >
              <ItemTooltip item={{ id: r.id, display_id: r.display_id, title: r.title, type: typeMap[r.type_id] }}>
                <IdChip id={r.id} displayId={r.display_id} showHumanIds={showHumanIds} disableTooltip />
              </ItemTooltip>
              <span className="palette-row-title">{r.title}</span>
              <TypeBadge label={typeMap[r.type_id]} />
              <ProjectBadge name={projectName(r)} />
            </PaletteRow>
          );
        })}
        {query.trim() && !loading && itemResults.length === 0 && (
          <div role="presentation" className="t-over" style={{ padding: '8px 10px' }}>
            {scope === 'vector'
              ? 'No semantic matches (the semantic index may still be building — see issue #223).'
              : 'No matching items.'}
          </div>
        )}

        {actions.length > 0 && (
          <div role="presentation" className="t-over" style={{ padding: '6px 10px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>
            Actions
          </div>
        )}
        {actions.map((a) => {
          const idx = flatEntries.findIndex((e) => e.kind === 'action' && e.data.id === a.id);
          return (
            <PaletteRow
              key={a.id}
              id={`cp-opt-${idx}`}
              active={idx === highlight}
              onMouseEnter={() => setHighlight(idx)}
              onClick={a.run}
              icon={<Ic name={a.icon} sm />}
            >
              <span className="palette-row-title">{a.label}</span>
            </PaletteRow>
          );
        })}
      </div>
    </Dialog>
  );
}

// Text ⟷ Vector scope toggle (COMMAND_PALETTE.md). A segmented control; `role="switch"`
// on the group with the two options as buttons. Mouse-down (not click) so it fires before
// the input's blur and keeps the palette open. Tab also toggles it (see handleKeyDown).
function PaletteRow({ id, active, onMouseEnter, onClick, icon, children }) {
  return (
    <div
      id={id}
      role="option"
      aria-selected={active}
      // #793 H: an `option` is an interactive role, so jsx-a11y requires it to be
      // focusable. This listbox is driven by `aria-activedescendant` (the search input
      // keeps DOM focus and points at the active option's `id`, see LISTBOX_ID above),
      // which is the APG combobox pattern — so the option must be focusABLE but must
      // NOT be a tab stop. `tabIndex={-1}` is exactly that distinction.
      tabIndex={-1}
      className={`snav${active ? ' active' : ''}`}
      style={{ height: 'auto', minHeight: 34, gap: 8, borderRadius: 6, cursor: 'pointer' }}
      onMouseEnter={onMouseEnter}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    >
      {icon}
      {children}
    </div>
  );
}
